import { useCallback, useMemo } from 'react'
import { Network, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchIpLists, removeIpEntry, SEC_STR as S, type BlacklistRow } from './securityMoreApi'

/**
 * A18.19 — IP чёрный/белый списки. LIVE `GET /admin/antifraud/blacklist`
 * (ip/device/phone/email/card entries); remove via `DELETE .../blacklist/:id`.
 */
export function IpListsScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<BlacklistRow>[]>(
    () => [
      {
        key: 'kind',
        header: S.kind,
        type: 'enum',
        width: 110,
        options: [
          { value: 'ip', label: 'IP', status: 'blocked' },
          { value: 'device', label: 'device', status: 'pending' },
          { value: 'phone', label: 'phone', status: 'review' },
          { value: 'email', label: 'email', status: 'review' },
          { value: 'card', label: 'card', status: 'banned' },
        ],
      },
      { key: 'value', header: S.value, type: 'text' },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 200 },
      { key: 'added_at', header: S.added, type: 'date', width: 150 },
    ],
    [],
  )

  const remove = useCallback(
    async (row: BlacklistRow) => {
      try {
        await removeIpEntry(row.id)
        toast(S.removed, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<BlacklistRow>[]>(
    () => [
      {
        id: 'remove',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: (r) => remove(r),
      },
    ],
    [remove],
  )

  const fetcher = useCallback(fetchIpLists, [])
  return (
    <>
      <PageHeader title={S.ipLists} screenId="A18.19" />
      <DataTable<BlacklistRow>
        screenId="A18.19"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <Network size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
